// Premium Design System for Elite Tourism Application
// Following the Elegance Formula and 58 UI Design Rules

export const DesignSystem = {
  // Color Palette - Following 60-30-10 Rule for Elite Tourism
  colors: {
    // Primary (60%) - Earthy beige/brown palette
    primary: {
      50: '#FEFCF9',   // Creamy white
      100: '#F5F2ED',  // Off white
      200: '#ECEBDE',  // Very light beige
      300: '#E0DDD0',  // Light beige
      400: '#D7D3BF',  // Medium light beige
      500: '#C1BAA1',  // Warm beige
      600: '#B5AD94',  // Medium beige
      700: '#A59D84',  // Medium brown
      800: '#8B8372',  // Dark brown
      900: '#6B6555',  // Very dark brown
    },
    
    // Secondary (30%) - Warm, premium accents (keeping warm tones)
    secondary: {
      50: '#FFF5F0',   // Cream
      100: '#FFE8DC',  // Light cream
      200: '#FFD4BA',  // Warm cream
      300: '#FFC4A0',  // Soft peach
      400: '#FFB088',  // Warm peach
      500: '#FF9D6F',  // Golden peach
      600: '#E67E50',  // Warm rust
      700: '#CC6B42',  // Rust
      800: '#B25833',  // Deep rust
      900: '#8B4513',  // Saddle brown
    },
    
    // Accent (10%) - Warm earth accents
    accent: {
      50: '#FBF9F7',   // Lightest cream
      100: '#F8F4EE',  // Very light cream
      200: '#F3EBE1',  // Light cream
      300: '#F0E6D2',  // Brighter soft cream
      400: '#ECD8B8',  // Bright medium cream
      500: '#E8CCAA',  // Bright warm beige
      600: '#D4B896',  // Golden beige (PRIMARY ACCENT)
      700: '#C9A584',  // Warm brown-beige
      800: '#B39175',  // Medium brown
      900: '#8B7355',  // Dark brown
    },
    
    // Semantic colors for status communication
    semantic: {
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    
    // Neutral palette for sophisticated backgrounds
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      gray: {
        50: '#FAFAFA',
        100: '#F4F4F5',
        200: '#E4E4E7',
        300: '#D4D4D8',
        400: '#A1A1AA',
        500: '#71717A',
        600: '#52525B',
        700: '#3F3F46',
        800: '#27272A',
        900: '#18181B',
      }
    }
  },

  // Typography - Premium, readable hierarchy with golden ratio
  typography: {
    fonts: {
      primary: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      secondary: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: '"Cormorant Garamond", Georgia, serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Droid Sans Mono", monospace',
    },
    
    // Clear hierarchy with golden ratio scaling (1.618x multiplier)
    scale: {
      h1: { size: '3.375rem', weight: 200, lineHeight: 1.1, letterSpacing: '-0.03em' },      // Dramatic hero heading
      h2: { size: '2.4rem', weight: 300, lineHeight: 1.15, letterSpacing: '-0.02em' },    // Section heading
      h3: { size: '1.65rem', weight: 400, lineHeight: 1.25, letterSpacing: '-0.01em' },    // Subsection
      h4: { size: '1.125rem', weight: 500, lineHeight: 1.4, letterSpacing: '0em' },         // Card heading
      h5: { size: '0.95rem', weight: 600, lineHeight: 1.5, letterSpacing: '0em' },        // Label
      h6: { size: '1rem', weight: 600, lineHeight: 1.5, letterSpacing: '0.01em' },        // Small heading
      body: { size: '1rem', weight: 400, lineHeight: 1.6, letterSpacing: '0em' },
      bodyLarge: { size: '1.125rem', weight: 400, lineHeight: 1.7, letterSpacing: '-0.01em' },
      bodySmall: { size: '0.875rem', weight: 400, lineHeight: 1.5, letterSpacing: '0.01em' },
      caption: { size: '0.75rem', weight: 500, lineHeight: 1.4, letterSpacing: '0.02em' },
      displayLarge: { size: '4.125rem', weight: 100, lineHeight: 1, letterSpacing: '-0.04em' }, // Premium hero
      displayMedium: { size: '2.85rem', weight: 200, lineHeight: 1.1, letterSpacing: '-0.03em' }, // Impact heading
    }
  },

  // Spacing system based on 8px grid with golden ratio
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
    '4xl': '6rem',   // 96px
    '5xl': '8rem',   // 128px
    '6xl': '12rem',  // 192px
  },

  // Sophisticated shadows for depth and elegance
  shadows: {
    subtle: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    soft: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    medium: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    large: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    luxe: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    glow: '0 0 0 1px rgba(255, 255, 255, 0.05), 0 0 20px rgba(255, 255, 255, 0.1)',
  },

  // Border radius for modern, clean aesthetics
  borderRadius: {
    none: '0',
    sm: '0.125rem',  // 2px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    '3xl': '1.5rem', // 24px
    full: '9999px',
  },

  // Animation and transition system for micro-interactions
  motion: {
    duration: {
      instant: '0ms',
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
      slower: '800ms',
    },
    
    easing: {
      ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    }
  },

  // Breakpoints for responsive design
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Z-index scale for layering
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  }
};

// Utility functions for design system
export const getColor = (path: string) => {
  const keys = path.split('.');
  let result: any = DesignSystem.colors;
  
  for (const key of keys) {
    result = result?.[key];
  }
  
  return result || DesignSystem.colors.neutral.gray[500];
};

export const getSpacing = (size: keyof typeof DesignSystem.spacing) => {
  return DesignSystem.spacing[size];
};

export const getShadow = (level: keyof typeof DesignSystem.shadows) => {
  return DesignSystem.shadows[level];
};

export const getTypography = (scale: keyof typeof DesignSystem.typography.scale) => {
  return DesignSystem.typography.scale[scale];
};

// Media query helpers
export const mediaQuery = {
  xs: `@media (min-width: ${DesignSystem.breakpoints.xs})`,
  sm: `@media (min-width: ${DesignSystem.breakpoints.sm})`,
  md: `@media (min-width: ${DesignSystem.breakpoints.md})`,
  lg: `@media (min-width: ${DesignSystem.breakpoints.lg})`,
  xl: `@media (min-width: ${DesignSystem.breakpoints.xl})`,
  '2xl': `@media (min-width: ${DesignSystem.breakpoints['2xl']})`,
};